import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@client-app/i18n";
import { authService } from "@common/api/authService";
import { PRODUCT_CONTEXT_HEADER } from "@common/api/productContext";
import {
  buildShopifyInstallCallbackUrl,
  readShopifyInstallContext,
} from "@/flows/shopifyInstall";
import { assertInstallWorkspace } from "@/flows/shopifyOnboardingInstall";
import {
  isValidShopifyAuthorizationUrl,
  postPopupInstallResult,
  recordPopupInstallShop,
  type PopupInstallIntent,
} from "@/flows/shopifyPopupInstall";
import { logSentryEvent } from "@/lib/sentry";

/**
 * Runs inside the Shopify popup (or a tab Shopify opened from the App Store).
 * It binds the installed shop to the originating workspace, then lets the
 * existing callback handler broadcast the result and close the window.
 */
export default function PopupShopifyInstall({
  intent,
}: {
  intent: PopupInstallIntent;
}) {
  const location = useLocation();
  const { t } = useTranslation();
  const [state, setState] = useState<"checking" | "done" | "error">(
    "checking",
  );
  useEffect(() => {
    const controller = new AbortController();
    const correlation = {
      provider: "shopify",
      attemptId: intent.attemptId,
      workspaceId: intent.workspaceId,
    };
    const requestConfig = {
      signal: controller.signal,
      headers: {
        [PRODUCT_CONTEXT_HEADER]: intent.productCode,
        "X-Workspace-Id": intent.workspaceId,
      },
    };
    setState("checking");
    // Defer one task so StrictMode setup/cleanup does not duplicate POSTs.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const ticket = readShopifyInstallContext(location.search);
          if (!ticket) throw new Error("Missing Shopify installation context");
          const workspace = await authService.getShopifyInstallWorkspace(
            intent.workspaceId,
            intent.productCode,
            controller.signal,
          );
          if (controller.signal.aborted) return;
          assertInstallWorkspace(workspace, intent.workspaceId);
          const context = await authService.resolveShopifyInstallContext(
            ticket,
            requestConfig,
          );
          if (controller.signal.aborted) return;
          if (
            context.oauth_app_id !== intent.oauthAppId ||
            !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(context.shop_domain)
          )
            throw new Error("Invalid Shopify installation context");
          const shop = context.shop_domain;
          recordPopupInstallShop(window.localStorage, intent, shop);
          const response = (await authService.connectShopify({
            install_context: ticket,
            shop_domain: shop,
            // No full-page marker: the existing callback handler broadcasts and closes.
            redirect_url: buildShopifyInstallCallbackUrl(
              window.location.origin,
              intent.workspaceId,
              intent.attemptId,
            ),
            connection_scene: intent.scene,
            axiosRequestConfig: requestConfig,
          })) as {
            authorizationUrl?: string;
            blockConnect?: boolean;
            alreadyConnected?: boolean;
            restrictionState?: string;
          } | undefined;
          if (controller.signal.aborted) return;
          if (
            response?.alreadyConnected === true ||
            response?.restrictionState === "already_connected"
          ) {
            postPopupInstallResult({ ...correlation, type: "SUCCESS", shop });
            setState("done");
            window.close();
            return;
          }
          if (response?.blockConnect)
            throw new Error("Shopify connection restricted");
          const authorizationUrl = response?.authorizationUrl || "";
          if (!isValidShopifyAuthorizationUrl(authorizationUrl, shop))
            throw new Error("Invalid Shopify authorization URL");
          window.location.assign(authorizationUrl);
        } catch {
          if (controller.signal.aborted) return;
          logSentryEvent(
            "shopify.popup_install.error",
            { scene: intent.scene },
            "warn",
          );
          postPopupInstallResult({
            ...correlation,
            type: "ERROR",
            message: "Shopify connection failed",
          });
          setState("error");
        }
      })();
    }, 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [intent.attemptId, location.search]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-6">
      <div className="max-w-md text-center text-sm leading-6 text-[#666F8D]">
        <p role={state === "error" ? "alert" : "status"} aria-live="polite">
          {t(
            state === "error"
              ? "website.shopifyConnectionEntry.popupError"
              : state === "done"
                ? "website.shopifyConnectionEntry.popupDone"
                : "website.shopifyInstall.checking",
          )}
        </p>
        {state !== "checking" && (
          <button
            type="button"
            className="mt-6 rounded-lg bg-[#1A162F] px-4 py-2 text-white"
            onClick={() => window.close()}
          >
            {t("website.shopifyConnectionEntry.popupClose")}
          </button>
        )}
      </div>
    </div>
  );
}
